/** Google Ads › Home: "Are we making money, and what needs me?" (blueprint §9.2, prototype #home). */
import type { OverviewResponse } from "@brecx/shared";
import { type ReactNode, useEffect, useState } from "react";
import { Link } from "react-router";
import { BarLineChart } from "../components/charts/BarLineChart";
import { Icon } from "../components/Icon";
import { Empty, ErrorBox, Kpi, Loading, Pace, Seg, TypeChip } from "../components/ui";
import { useApi } from "../lib/api";
import { delta, money, pct, ratio, shortDate } from "../lib/format";
import { useRange } from "../lib/range";
import { useSession } from "../lib/session";
import { HomeAiCard } from "./HomeAiCard";

const n = (m: string) => Number(m) / 1e6;
const tone = (m: string) => (Number(m) > 0 ? "pos" : Number(m) < 0 ? "neg" : "");

export function Home() {
  const { query, days } = useRange();
  const { can } = useSession();
  const { data, error, loading, reload } = useApi<OverviewResponse>(`/overview?${query}`);
  const [view, setView] = useState<"chart" | "table">("chart");
  useEffect(() => {
    document.title = "Home · Brecx Ads";
  }, []);

  return (
    <div className="page">
      <h1 className="sr-only">Home</h1>

      {error ? <ErrorBox error={error} retry={reload} /> : null}
      {loading && !data ? <Loading rows={3} /> : null}
      {data ? (
        <HomeBody
          d={data}
          view={view}
          setView={setView}
          query={query}
          ai={
            can("manager") ? (
              <HomeAiCard days={days} monthFraction={data.pacing.expectedMonthFraction} />
            ) : null
          }
        />
      ) : null}
    </div>
  );
}

function HomeBody({
  d,
  view,
  setView,
  query,
  ai,
}: {
  d: OverviewResponse;
  view: "chart" | "table";
  setView: (v: "chart" | "table") => void;
  query: string;
  /** the AI cost card (managers and up) */
  ai: ReactNode;
}) {
  const k = d.kpis;
  const s = d.series;
  const hasMetrics = s.some((r) => r.costMicros !== "0");
  const p = d.pacing;
  return (
    <>
      <div className="health" role="list" aria-label="Account health">
        {d.health.map((h) => (
          <div className="hc" role="listitem" key={h.key}>
            <span className={`st ${h.status}`}>
              <Icon name={h.status === "good" ? "check" : h.status === "warn" ? "alert" : "x"} size={11} />
            </span>
            <b>{h.label}</b>
            <span>{h.detail}</span>
          </div>
        ))}
      </div>

      <div className="kpis">
        <Kpi
          label="Ad spend"
          value={money(k.cost.micros)}
          delta={delta(k.cost.deltaPct)}
          sub={`${k.campaignsWithSpend} campaigns`}
          spark={s.map((r) => n(r.costMicros))}
          formula={
            <>
              Sum of Google Ads cost for {d.range.from} → {d.range.to}, compared with the previous {s.length}{" "}
              days.
            </>
          }
        />
        <Kpi
          label="Conv. value"
          value={money(k.convValue.micros)}
          delta={delta(k.convValue.deltaPct)}
          sub={`${k.convValue.conversions.toLocaleString()} orders`}
          spark={s.map((r) => n(r.valueMicros))}
          formula="Conversion value reported by Google Ads for purchase conversions."
        />
        <Kpi
          hero
          label="Gross profit after ads"
          value={money(k.grossProfitAfterAds.micros)}
          delta={delta(k.grossProfitAfterAds.deltaPct)}
          sub="after COGS, shipping, fees, returns"
          spark={s.map((r) => n(r.profitAfterAdsMicros))}
          formula={
            <>
              <code>conv. value − (COGS + shipping + fees + returns) − ad spend</code>Costs come from each
              linked product's economics (Profit model); unlinked campaigns use the catalog average.
            </>
          }
        />
        <Kpi
          label="POAS"
          value={ratio(k.poas.value)}
          delta={delta(k.poas.delta, { points: true })}
          sub={`target ${k.poas.target.toFixed(2)} · break-even 1.00`}
          spark={s.map((r) => r.poas ?? 0)}
          formula={
            <>
              <code>gross profit before ads ÷ ad spend</code>Above 1.00 the ads pay for themselves.
            </>
          }
        />
        <Kpi
          label="ROAS"
          value={pct(k.roas.pct)}
          delta={delta(k.roas.deltaPct)}
          sub="conv. value ÷ spend"
          spark={s.map((r) => r.roasPct ?? 0)}
          formula="Revenue return on ad spend. Google bids to this; profit decisions use POAS."
        />
        <Kpi
          label="Wasted spend"
          value={money(k.wastedSpend.micros)}
          delta={
            k.wastedSpend.deltaPts === null
              ? null
              : delta(k.wastedSpend.deltaPts, { points: true, lowerIsBetter: true })
          }
          sub={
            k.wastedSpend.sharePct === null
              ? "no search terms yet"
              : `${k.wastedSpend.sharePct.toFixed(1)}% of search-term spend`
          }
          formula="Cost of search terms with 0 conversions over the range."
        />
      </div>

      <div className="grid g-main">
        <div className="stack">
          <div className="card">
            <div className="card-h">
              <h2>Ad spend vs gross profit after ads, by day</h2>
              <div className="row" style={{ gap: 12 }}>
                <div className="legend">
                  <span>
                    <i style={{ background: "var(--s2)" }} />
                    Ad spend
                  </span>
                  <span>
                    <i style={{ background: "var(--profit)" }} />
                    Profit after ads
                  </span>
                </div>
                <Seg
                  label="Show as"
                  value={view}
                  onChange={setView}
                  options={[
                    ["chart", "Chart"],
                    ["table", "Table"],
                  ]}
                />
              </div>
            </div>
            <div className="card-b">
              {!hasMetrics ? (
                <Empty>
                  No metrics yet. They arrive from the daily Google Ads read-back once campaigns serve.
                  Everything here is a real zero, not a placeholder.
                </Empty>
              ) : view === "chart" ? (
                <BarLineChart
                  title="Ad spend vs gross profit after ads"
                  dates={s.map((r) => r.date)}
                  bars={s.map((r) => n(r.costMicros))}
                  line={s.map((r) => n(r.profitAfterAdsMicros))}
                  barLabel="Ad spend"
                  lineLabel="Profit after ads"
                />
              ) : (
                <div className="tbl-wrap" style={{ maxHeight: 320 }}>
                  <table className="tbl compact">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th className="n">Ad spend</th>
                        <th className="n">Profit after ads</th>
                        <th className="n">POAS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...s].reverse().map((r) => (
                        <tr key={r.date}>
                          <td>{shortDate(r.date)}</td>
                          <td className="n">{money(r.costMicros)}</td>
                          <td className={`n ${tone(r.profitAfterAdsMicros)}`}>
                            {money(r.profitAfterAdsMicros)}
                          </td>
                          <td className="n">{ratio(r.poas)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-h">
              <h2>Campaigns by spend</h2>
              <Link className="hint linkish" to={`/campaigns?${query}`}>
                All campaigns
              </Link>
            </div>
            <div className="card-b flush">
              {d.campaigns.length ? (
                <div className="tbl-wrap">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Campaign</th>
                        <th className="n">Spend</th>
                        <th className="n">Profit after ads</th>
                        <th className="n">POAS</th>
                        <th className="n">ROAS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.campaigns.map((c) => (
                        <tr key={c.key}>
                          <td>
                            <TypeChip kind={c.channelType} />{" "}
                            <Link to={`/campaigns?c=${encodeURIComponent(c.key)}&${query}`}>{c.name}</Link>
                          </td>
                          <td className="n">{money(c.metrics.costMicros)}</td>
                          <td className={`n ${tone(c.metrics.profitAfterAdsMicros)}`}>
                            {money(c.metrics.profitAfterAdsMicros)}
                          </td>
                          <td className="n">{ratio(c.metrics.poas)}</td>
                          <td className="n">{pct(c.metrics.roasPct)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Empty>No campaign has spent in this range.</Empty>
              )}
            </div>
          </div>
        </div>

        <div className="stack">
          <div className="card">
            <div className="card-h">
              <h2>Pacing</h2>
            </div>
            <div className="card-b stack">
              <Pace
                label="Today"
                value={`${money(p.todaySpendMicros)} of ${money(p.dailyBudgetMicros)}`}
                frac={n(p.dailyBudgetMicros) ? n(p.todaySpendMicros) / n(p.dailyBudgetMicros) : 0}
                expected={p.expectedTodayFraction}
              />
              <Pace
                label="This month"
                value={`${money(p.monthSpendMicros)} of ${money(p.monthPlanMicros)}`}
                frac={n(p.monthPlanMicros) ? n(p.monthSpendMicros) / n(p.monthPlanMicros) : 0}
                expected={p.expectedMonthFraction}
              />
              <p className="muted" style={{ fontSize: 12 }}>
                The dark marker is where spend should be by now. Set a monthly plan in{" "}
                <Link className="linkish" to={`/automations?tab=guardrails&${query}`}>
                  Guardrails
                </Link>
                .
              </p>
            </div>
          </div>

          <div className="card">
            <div className="card-h">
              <h2>Needs you</h2>
              <Link className="hint linkish" to={`/inbox?${query}`}>
                Suggestions
              </Link>
            </div>
            <div className="card-b flush">
              {d.needsYou.length ? (
                <div className="list">
                  {d.needsYou.map((x) => (
                    <Link className="li" key={x.id} to={`${x.href}&${query}`}>
                      <span className={`ic ${x.kind === "alert" ? "warn" : "acc"}`}>
                        <Icon name={x.kind === "alert" ? "alert" : x.kind === "change_set" ? "git" : "zap"} />
                      </span>
                      <span className="bd">
                        <span className="t">{x.title}</span>
                        {x.why ? <span className="s">{x.why}</span> : null}
                      </span>
                    </Link>
                  ))}
                </div>
              ) : (
                <Empty>Nothing needs you right now.</Empty>
              )}
            </div>
          </div>

          {ai}

          <div className="card">
            <div className="card-h">
              <h2>Autopilot, last 24 h</h2>
              <Link className="hint linkish" to={`/changes?tab=audit&${query}`}>
                Audit log
              </Link>
            </div>
            <div className="card-b flush">
              {d.autopilot.length ? (
                <div className="list feed">
                  {d.autopilot.map((a) => (
                    <div className="li" key={`${a.at}-${a.label}`}>
                      <time>
                        {new Date(a.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </time>
                      <span className="bd">
                        <span className="t">{a.label}</span>
                        <span className="s">
                          {a.actor}
                          {a.simulated ? " · simulated (Safe mode)" : ""}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty>No change was applied in the last 24 hours.</Empty>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
